/* One comparison selection for cards, product pages, the page and native modal.
   Only public product references live in session storage. Prices, inventory and
   specifications are always rendered by Shopify in the active buyer context. */
(() => {
  "use strict";
  if (window.SaberCompare) return;
  const LIMIT = 4,
    EVENT = "saber:compare-changed",
    KEY = "compare-products";
  let memory = Object.create(null);
  const safeHandle = (value) =>
    typeof value === "string" && /^[a-z0-9][a-z0-9-]{0,200}$/.test(value);
  const safeVariant = (value) =>
    /^\d{1,20}$/.test(String(value || "")) ? String(value) : "";
  function rootPath() {
    return (window.routes?.root_url || "/").replace(/\/$/, "") + "/";
  }
  function ref(value) {
    if (typeof value !== "string") return null;
    const pieces = value.split("~");
    if (
      pieces.length > 2 ||
      !safeHandle(pieces[0]) ||
      (pieces[1] && !safeVariant(pieces[1]))
    )
      return null;
    return { handle: pieces[0], variantId: safeVariant(pieces[1]) };
  }
  const refKey = (item) =>
    item.handle + (item.variantId ? "~" + item.variantId : "");
  function productUrl(item) {
    const url = new URL(
      rootPath() + "products/" + item.handle,
      location.origin,
    );
    if (item.variantId) url.searchParams.set("variant", item.variantId);
    return url;
  }
  function read() {
    let source = memory;
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) source = JSON.parse(raw);
    } catch (_) {
      /* Usable memory fallback. */
    }
    const result = Object.create(null);
    if (!source || typeof source !== "object" || Array.isArray(source))
      return result;
    for (const [key, value] of Object.entries(source).slice(0, 50)) {
      const item = ref(key) || ref(value?.handle);
      if (!item || !value || typeof value !== "object") continue;
      const variantId = safeVariant(value.variantId) || item.variantId;
      const normalized = {
        handle: item.handle,
        variantId,
        title: String(value.title || "").slice(0, 300),
      };
      normalized.url =
        productUrl(normalized).pathname + productUrl(normalized).search;
      result[refKey(normalized)] = normalized;
      if (Object.keys(result).length === LIMIT) break;
    }
    return result;
  }
  function write(products) {
    memory = products;
    try {
      sessionStorage.setItem(KEY, JSON.stringify(products));
    } catch (_) {
      /* Session still works in memory. */
    }
    document.dispatchEvent(new CustomEvent(EVENT));
  }
  function add(item) {
    if (!safeHandle(item.handle)) return false;
    const products = read(),
      key = refKey(item);
    if (!(key in products) && Object.keys(products).length >= LIMIT)
      return false;
    products[key] = {
      handle: item.handle,
      variantId: safeVariant(item.variantId),
      title: String(item.title || "").slice(0, 300),
    };
    write(products);
    return true;
  }
  function remove(key) {
    const products = read();
    delete products[key];
    write(products);
  }
  function compareUrl(items = Object.values(read())) {
    const url = new URL(rootPath(), location.origin);
    url.searchParams.set("view", "compare-products");
    for (const item of items.slice(0, LIMIT))
      url.searchParams.append("handles", refKey(item));
    return url.pathname + url.search;
  }
  function status(node, message, error = false) {
    const target = node.querySelector("[data-compare-status]");
    if (target) {
      target.textContent = message;
      target.setAttribute("role", error ? "alert" : "status");
    }
  }
  const api = (window.SaberCompare = {
    read,
    write,
    add,
    remove,
    compareUrl,
    ref,
    refKey,
    limit: LIMIT,
  });
  window.getCompareProducts = read;
  function definition(name, klass) {
    if (!customElements.get(name)) customElements.define(name, klass);
  }
  function syncLinks() {
    document
      .querySelectorAll("[data-compare-selected-link]")
      .forEach((link) => {
        link.href = compareUrl();
        const count = Object.keys(read()).length;
        link.textContent = count
          ? "View comparison (" + count + ") →"
          : "View comparison →";
      });
  }
  document.addEventListener(EVENT, syncLinks);
  document.addEventListener("DOMContentLoaded", syncLinks);
  definition(
    "product-compare-checkbox",
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.input = this.querySelector('input[type="checkbox"]');
        this.item = {
          handle: this.dataset.handle,
          variantId: safeVariant(this.dataset.variantId),
          title: this.dataset.title || "",
        };
        this.key = refKey(this.item);
        this.refresh = () => {
          this.input.checked = this.key in read();
          this.querySelectorAll("a").forEach((link) => {
            link.href = compareUrl();
          });
        };
        this.changed = () => {
          if (this.input.checked && !add(this.item)) {
            this.input.checked = false;
            const alert = this.querySelector("[data-compare-limit]");
            if (alert) {
              alert.hidden = false;
              alert.textContent =
                "Compare up to 4 products. Remove one from your comparison first.";
            }
            return;
          }
          if (!this.input.checked) remove(this.key);
          const note = this.querySelector("[data-compare-limit]");
          if (note) note.hidden = true;
          this.querySelector(".card-product-compare-alert")?.classList.toggle(
            "visible",
            this.input.checked,
          );
        };
        this.input?.addEventListener("change", this.changed);
        document.addEventListener(EVENT, this.refresh);
        this.refresh();
      }
      disconnectedCallback() {
        document.removeEventListener(EVENT, this.refresh);
        this.input?.removeEventListener("change", this.changed);
        this.ready = false;
      }
      setChecked(value) {
        if (this.input) this.input.checked = value;
      }
      setDisabled(value) {
        if (this.input) this.input.disabled = value;
      }
    },
  );
  definition(
    "product-compare-buttons",
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.action = this.querySelector(".collection-actions-compare-action");
        this.link = this.querySelector(
          "a.collection-actions-compare-button-label",
        );
        this.toggle = this.querySelector(
          "input.collection-action-compare-toggle-checkbox",
        );
        this.dismiss = this.querySelector(
          ".collection-actions-compare-button-dismiss",
        );
        this.update = () => {
          const count = Object.keys(read()).length;
          if (this.action)
            this.action.hidden =
              !count || !!(this.toggle && !this.toggle.checked);
          if (this.link) {
            this.link.href = compareUrl();
            this.link.setAttribute("aria-disabled", String(count < 2));
            const n = this.link.querySelector(
              ".collection-actions-compare-value",
            );
            if (n) n.textContent = count;
          }
          this.dataset.maxProducts = LIMIT;
          document
            .querySelectorAll("product-compare-checkbox a")
            .forEach((link) => (link.href = compareUrl()));
        };
        this.clear = (event) => {
          event.preventDefault();
          write(Object.create(null));
        };
        this.click = (event) => {
          if (Object.keys(read()).length < 2) event.preventDefault();
        };
        this.change = () => {
          document.body.classList.toggle(
            "product-compare-toggled-on",
            this.toggle.checked,
          );
          try {
            sessionStorage.setItem(
              "compareMasterToggleState",
              String(this.toggle.checked),
            );
          } catch (_) {}
          this.update();
        };
        this.dismiss?.addEventListener("click", this.clear);
        this.link?.addEventListener("click", this.click);
        this.toggle?.addEventListener("change", this.change);
        if (this.toggle) {
          try {
            const saved = sessionStorage.getItem("compareMasterToggleState");
            if (saved !== null) this.toggle.checked = saved === "true";
          } catch (_) {}
          this.change();
        } else document.body.classList.add("product-compare-toggled-on");
        document.addEventListener(EVENT, this.update);
        this.update();
        document.body.classList.remove("page-loading");
      }
      disconnectedCallback() {
        document.removeEventListener(EVENT, this.update);
        this.dismiss?.removeEventListener("click", this.clear);
        this.link?.removeEventListener("click", this.click);
        this.toggle?.removeEventListener("change", this.change);
        this.ready = false;
      }
    },
  );
  definition(
    "product-compare",
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.container = this.querySelector(".product-compare-grid");
        if (!this.container) return;
        this.handleDismiss = (event) => {
          const button = event.target.closest(".product-dismiss-button");
          if (!button) return;
          const col = button.closest(".product-compare-product");
          remove(col.dataset.compareKey || col.dataset.handle);
          col.remove();
          if (this.dataset.enhanced === "true") {
            const remaining = [
              ...this.container.querySelectorAll(".product-compare-product"),
            ].map(
              (el) =>
                ref(el.dataset.compareKey) || {
                  handle: el.dataset.handle,
                  variantId: safeVariant(el.dataset.variantId),
                },
            );
            this.dataset.handles = JSON.stringify(remaining.map(refKey));
            history.replaceState(null, "", compareUrl(remaining));
          }
          this.updateColumnCount();
          this.removeEmptyLines();
          this.updateDismissButtonVisibility();
          this.updateMessage();
        };
        this.clear = () => {
          this.abort?.abort();
          this.container
            .querySelectorAll(".product-compare-product")
            .forEach((col) => col.remove());
          write(Object.create(null));
          this.dataset.handles = "[]";
          history.replaceState(null, "", compareUrl([]));
          this.updateColumnCount();
          this.removeEmptyLines();
          this.updateMessage();
        };
        this.refresh = () => this.loadComparisonProducts(true);
        this.retry = () => this.loadComparisonProducts(true);
        this.container.addEventListener("click", this.handleDismiss);
        this.querySelector("[data-compare-clear]")?.addEventListener(
          "click",
          this.clear,
        );
        this.querySelector("[data-compare-retry]")?.addEventListener(
          "click",
          this.retry,
        );
        this.focus = () => {
          if (document.visibilityState === "visible") this.refresh();
        };
        document.addEventListener("visibilitychange", this.focus);
        this.pageShow = (event) => {
          if (event.persisted) this.refresh();
        };
        window.addEventListener("pageshow", this.pageShow);
        this.loadComparisonProducts();
      }
      disconnectedCallback() {
        this.abort?.abort();
        this.container?.removeEventListener("click", this.handleDismiss);
        this.querySelector("[data-compare-clear]")?.removeEventListener(
          "click",
          this.clear,
        );
        this.querySelector("[data-compare-retry]")?.removeEventListener(
          "click",
          this.retry,
        );
        document.removeEventListener("visibilitychange", this.focus);
        window.removeEventListener("pageshow", this.pageShow);
        this.ready = false;
      }
      updateColumnCount() {
        this.container.style.setProperty(
          "--compare-product-count",
          this.container.querySelectorAll(".product-compare-product").length,
        );
      }
      updateDismissButtonVisibility() {
        this.container
          .querySelectorAll(".product-dismiss-button")
          .forEach((button) => (button.hidden = false));
      }
      updateMessage() {
        const count = this.container.querySelectorAll(
          ".product-compare-product",
        ).length;
        status(
          this,
          count < 2
            ? "Choose at least 2 products using Compare on product pages or collection cards."
            : "Comparing " +
                count +
                " products. Prices and availability reflect your current shopping account.",
        );
        const clear = this.querySelector("[data-compare-clear]");
        if (clear) clear.hidden = !count;
      }
      removeEmptyLines() {
        const groups = new Map();
        this.container
          .querySelectorAll(".product-compare-item-row[data-index]")
          .forEach((cell) => {
            const i = cell.dataset.index;
            if (!groups.has(i)) groups.set(i, []);
            groups.get(i).push(cell);
          });
        for (const [i, cells] of groups) {
          if (i === "0") continue;
          const values = cells.filter((cell) =>
            cell.classList.contains("product-compare-value"),
          );
          const empty =
            values.length > 0 &&
            values.every(
              (cell) =>
                !cell.textContent.trim() &&
                !cell.querySelector("img,iframe,video"),
            );
          for (const cell of cells)
            cell.classList.toggle("compare-row-hidden", empty);
        }
      }
      async loadComparisonProducts(force = false) {
        this.abort?.abort();
        this.abort = new AbortController();
        const signal = this.abort.signal;
        const existing = [
          ...this.container.querySelectorAll(
            ".product-compare-product:not(.product-compare-item-preview)",
          ),
        ];
        let raw = [];
        try {
          raw = this.dataset.handles
            ? JSON.parse(this.dataset.handles)
            : new URLSearchParams(location.search).getAll("handles");
        } catch (_) {}
        if (!Array.isArray(raw)) raw = [];
        let refs = raw.map(ref).filter(Boolean);
        if (!refs.length && !existing.length) refs = Object.values(read());
        const base = existing.map(
          (col) =>
            ref(col.dataset.compareKey) || {
              handle: col.dataset.handle,
              variantId: safeVariant(col.dataset.variantId),
            },
        );
        refs = [...base, ...refs]
          .filter(
            (item, index, all) =>
              safeHandle(item.handle) &&
              all.findIndex((other) => refKey(other) === refKey(item)) ===
                index,
          )
          .slice(0, LIMIT);
        if (!force && existing.length > 0)
          refs = refs.filter(
            (item) => !base.some((other) => refKey(other) === refKey(item)),
          );
        if (!refs.length) {
          this.classList.add("is-loaded");
          this.updateColumnCount();
          this.removeEmptyLines();
          this.updateDismissButtonVisibility();
          this.updateMessage();
          return;
        }
        this.querySelectorAll(".product-compare-item-preview").forEach((el) =>
          el.remove(),
        );
        // Hide all old pricing during a recheck. Failed/context-changed data never remains visible.
        if (force) this.container.hidden = true;
        this.setAttribute("aria-busy", "true");
        status(this, "Loading current product details…");
        const context = this.dataset.compareContext || "";
        try {
          const cols = await Promise.all(
            refs.map(async (item) => {
              const url = productUrl(item);
              url.searchParams.set("view", "compare");
              if (this.dataset.nativeSectionId)
                url.searchParams.set(
                  "section_id",
                  this.dataset.nativeSectionId,
                );
              const response = await fetch(url.pathname + url.search, {
                credentials: "same-origin",
                cache: "no-store",
                headers: { Accept: "text/html" },
                signal,
              });
              if (!response.ok)
                throw new Error(
                  "A product could not be loaded. It may no longer be available.",
                );
              if (
                response.redirected &&
                new URL(response.url).origin !== location.origin
              )
                throw new Error("Refresh this page to compare products.");
              const html = await response.text();
              if (html.length > 2000000)
                throw new Error(
                  "A product response is too large. Open its product page instead.",
                );
              const doc = new DOMParser().parseFromString(html, "text/html");
              const col = [
                ...doc.querySelectorAll(".product-compare-product"),
              ].find((el) => el.dataset.handle === item.handle);
              if (!col)
                throw new Error(
                  "A product could not be loaded. It may no longer be available.",
                );
              if (context && col.dataset.compareContext !== context)
                throw new Error(
                  "Your account, company location or currency changed. Refresh this page before comparing prices.",
                );
              if (item.variantId && col.dataset.variantId !== item.variantId)
                throw new Error(
                  "A selected product option is no longer available. Choose its options again.",
                );
              col.dataset.compareKey = refKey(item);
              col.querySelectorAll("script").forEach((el) => el.remove());
              return col;
            }),
          );
          if (signal.aborted) return;
          if (force) existing.forEach((col) => col.remove());
          cols.forEach((col) =>
            this.container.appendChild(document.importNode(col, true)),
          );
          this.container.hidden = false;
          this.classList.add("is-loaded");
          this.updateColumnCount();
          this.removeEmptyLines();
          this.updateDismissButtonVisibility();
          this.updateMessage();
        } catch (error) {
          if (signal.aborted) return;
          this.container.hidden = true;
          this.classList.add("is-loaded");
          status(
            this,
            error.message ||
              "The comparison could not be loaded. Please try again.",
            true,
          );
        } finally {
          if (!signal.aborted) this.removeAttribute("aria-busy");
        }
      }
      hidePreviewIfEnoughProducts() {
        this.querySelectorAll(".product-compare-item-preview").forEach((el) =>
          el.remove(),
        );
      }
    },
  );
  // Existing theme modal is retained for older links, but new actions use the
  // normal comparison page so browser Back, sharing and keyboard navigation work.
  const BaseModal =
    typeof ModalDialog !== "undefined" ? ModalDialog : HTMLElement;
  definition(
    "product-compare-modal",
    class extends BaseModal {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
      }
      async show(opener) {
        if (super.show) super.show(opener);
        else this.setAttribute("open", "");
        this.classList.add("is-loading");
        const target = this.querySelector(
          ".product-compare-modal-dialog-content",
        );
        try {
          const url = new URL(this.dataset.url, location.origin);
          if (url.origin !== location.origin)
            throw new Error("Invalid comparison link.");
          const response = await fetch(url.pathname + url.search, {
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "text/html" },
          });
          if (!response.ok) throw new Error("Unable to load comparison.");
          const doc = new DOMParser().parseFromString(
            await response.text(),
            "text/html",
          );
          const content = doc.querySelector(".product-compare-page");
          if (!content) throw new Error("Unable to load comparison.");
          const component = content.querySelector("product-compare");
          if (component)
            component.dataset.handles = JSON.stringify(
              url.searchParams.getAll("handles"),
            );
          target.replaceChildren(document.importNode(content, true));
        } catch (error) {
          if (target) target.textContent = error.message;
        } finally {
          this.classList.remove("is-loading");
        }
      }
      hide() {
        if (super.hide) super.hide();
        else this.removeAttribute("open");
        this.querySelector(
          ".product-compare-modal-dialog-content",
        )?.replaceChildren();
      }
    },
  );
  syncLinks();
})();
